/**
 * Arabic render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'ar')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const arabicLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'ضبابية' },
    change: { primary: 'تغيير' },
    click: { primary: 'نقر' },
    dblclick: { primary: 'dblclick' },
    focus: { primary: 'تركيز' },
    input: { primary: 'إدخال' },
    keydown: { primary: 'مفتاح أسفل' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'مفتاح أعلى' },
    load: { primary: 'تحميل' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'mouseout' },
    mouseover: { primary: 'فأرة فوق' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'reset' },
    resize: { primary: 'تغيير حجم' },
    scroll: { primary: 'تمرير' },
    submit: { primary: 'إرسال' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 'و' },
    bind: { primary: 'اربط' },
    changes: { primary: 'يتغير' },
    contains: { primary: 'يحتوي' },
    else: { primary: 'وإلا' },
    end: { primary: 'النهاية' },
    equals: { primary: 'يساوي' },
    exists: { primary: 'موجود' },
    has: { primary: 'لديه' },
    have: { primary: 'لدي' },
    includes: { primary: 'يشمل' },
    is: { primary: 'هو' },
    live: { primary: 'حي' },
    matches: { primary: 'يطابق' },
    not: { primary: 'ليس' },
    or: { primary: 'أو' },
    otherwise: { primary: 'خلاف ذلك' },
    then: { primary: 'ثم' },
    when: { primary: 'عندما' },
    where: { primary: 'أين' },
  },
  temporal: {
    h: { primary: 'س' },
    hour: { primary: 'ساعة' },
    hours: { primary: 'ساعات' },
    millisecond: { primary: 'ميلي ثانية' },
    milliseconds: { primary: 'ميلي ثانية' },
    min: { primary: 'د' },
    minute: { primary: 'دقيقة' },
    minutes: { primary: 'دقائق' },
    ms: { primary: 'م.ث' },
    s: { primary: 'ث' },
    second: { primary: 'ثانية' },
    seconds: { primary: 'ثوانِ' },
    times: { primary: 'مرات' },
  },
  values: {
    body: { primary: 'جسم' },
    detail: { primary: 'تفاصيل' },
    document: { primary: 'وثيقة' },
    element: { primary: 'عنصر' },
    event: { primary: 'حدث' },
    false: { primary: 'خطأ' },
    it: { primary: 'هو' },
    its: { primary: 'له' },
    me: { primary: 'أنا' },
    my: { primary: 'لي' },
    myself: { primary: 'نفسي' },
    null: { primary: 'null' },
    result: { primary: 'نتيجة' },
    target: { primary: 'هدف' },
    true: { primary: 'صحيح' },
    undefined: { primary: 'غير معرف' },
    value: { primary: 'قيمة' },
    window: { primary: 'نافذة' },
    you: { primary: 'أنت' },
    your: { primary: 'لك' },
    yourself: { primary: 'نفسك' },
  },
  attributes: {
    attribute: { primary: 'خاصية' },
    attributes: { primary: 'خصائص' },
    class: { primary: 'فئة' },
    classes: { primary: 'فئات' },
    properties: { primary: 'خصائص' },
    property: { primary: 'خاصية' },
    style: { primary: 'أسلوب' },
    styles: { primary: 'أساليب' },
  },
  expressions: {
    at: { primary: 'عند' },
    characters: { primary: 'حروف' },
    children: { primary: 'أطفال' },
    closest: { primary: 'الأقرب' },
    empty: { primary: 'فارغ' },
    'ends with': { primary: 'ينتهي بـ' },
    exclusive: { primary: 'حصري' },
    first: { primary: 'أول' },
    'ignoring case': { primary: 'مع تجاهل الحالة' },
    inclusive: { primary: 'شامل' },
    'joined by': { primary: 'مدموج بواسطة' },
    last: { primary: 'آخر' },
    'mapped to': { primary: 'محول إلى' },
    next: { primary: 'التالي' },
    no: { primary: 'لا يوجد' },
    parent: { primary: 'والد' },
    prev: { primary: 'سابق' },
    previous: { primary: 'السابق' },
    random: { primary: 'عشوائي' },
    some: { primary: 'بعض' },
    'sorted by': { primary: 'مرتب حسب' },
    'split by': { primary: 'مقسم بواسطة' },
    'starts with': { primary: 'يبدأ بـ' },
    within: { primary: 'داخل' },
  },
};

registerLexicon('ar', arabicLexicon);
